// React
import { useContext } from "react"

// Components
import { BaseTable, Button, Modal } from "components"

// Contexts
import { DataContext } from "../contexts"

// Third-Party Libraries
import toast from "react-hot-toast"
import { read, utils } from "xlsx"

// Types
import type { DataContextType } from "../types"

// Utils
import { useToggle } from "utils"
import { useDataImport } from "../utils"

interface Type extends DataContextType {
  fetchMasterData: {
    isError: boolean
    isLoading: boolean
  }
}

export function UploadFile(params: Type): JSX.Element {
  // Hooks
  const { isActive, toggle } = useToggle(false)
  const { isError, isLoading } = params.fetchMasterData

  return (
    <>
      <Button
        color="primary"
        onClick={toggle}
        loading={isLoading ? "true" : undefined}
        disabled={isLoading || isError}
      >
        {isLoading ? "FETCHING ALL MASTER DATA" : isError ? "FETCH DATA ERROR" : "UPLOAD FILE"}
      </Button>

      {isActive && (
        <DataContext.Provider value={params}>
          <ModalSection toggle={toggle} />
        </DataContext.Provider>
      )}
    </>
  )
}

function ModalSection(params: {
  toggle: () => void
}): JSX.Element {
  // Hooks
  const { blankExample, list, assignData } = useContext(DataContext)
  const { data, setData } = useDataImport()

  // Vars
  const headers: string[] = Object.keys(data[0] ?? {})

  return (
    <Modal
      isOpen
      title="UPLOAD FILE"
      closeModal={params.toggle}
    >
      <Modal.Body contentClassName="flex flex-col gap-3">
        <section>
          <label
            htmlFor="upload"
            className="btn btn-primary rounded-none"
          >
            SELECT FILE
          </label>

          <input
            hidden
            type="file"
            name="upload"
            id="upload"
            onChange={(e) => {
              if (e.target.files?.length) {
                const reader = new FileReader()
                reader.onload = (e) => {
                  if (e.target) {
                    const data = e.target.result
                    const workbook = read(data, { type: "array" })
                    const sheetName = workbook.SheetNames[0]
                    const worksheet = workbook.Sheets[sheetName]
                    const headers: string[] = utils.sheet_to_json(worksheet, { header: 1 })[0] as string[]
                    const json = utils.sheet_to_json(worksheet)

                    if (!json.length) {
                      return toast.error("File is empty")
                    }

                    // Vars
                    const prev: any[] = json
                    const headerData: { [key: string]: any } = {}

                    headers.map(item => {
                      headerData[item] = blankExample[item]
                      return null
                    })
                    setData(prev.map(item => {
                      return {
                        ...headerData,
                        ...item,
                      }
                    }))
                  }
                }

                reader.readAsArrayBuffer(e.target.files[0])
                e.target.value = ""
              }
            }}
          />
        </section>

        {data.length ? (
          <section>
            <div className="font-bold">FILE PREVIEW</div>

            <div className="overflow-auto">
              <BaseTable>
                <thead>
                  <tr>
                    {headers.map((item, key) => <th key={key}>{item}</th>)}
                  </tr>
                </thead>

                <tbody>
                  {data.map((item, key) => (
                    <tr key={key}>
                      {headers.map((i, key) => <td key={key}>{item[i]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </BaseTable>
            </div>
          </section>
        ) : (
          <></>
        )}
      </Modal.Body>

      <Modal.Footer>
        <section className="flex justify-between items-center">
          <div>{data.length ? `Total data: ${data.length} items` : ""}</div>

          <Button
            color="primary"
            onClick={() => {
              // Vars
              const header = headers.find(item => !list.map(item => item.value).includes(item))

              if (!data.length) {
                return toast.error("No data available")
              }

              if (header) {
                return toast.error(`${header} is not available on the format`)
              }

              assignData(data)
              params.toggle()
            }}
          >
            SAVE
          </Button>
        </section>
      </Modal.Footer>
    </Modal>
  )
}